import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Customer, BusinessType } from '@/types';
import { useAppContext } from '@/contexts/AppContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { supabase } from '@/lib/supabase';

interface CustomerFormProps {
  open: boolean;
  onClose: () => void;
  onSave: (customer: Omit<Customer, 'id'>) => Promise<void>;
  customer?: Customer;
}

const CustomerForm: React.FC<CustomerFormProps> = ({ open, onClose, onSave, customer }) => {
  const { currentUser } = useAppContext();
  const isMobile = useIsMobile();
  const [businessTypes, setBusinessTypes] = useState<BusinessType[]>([]);
  const [formData, setFormData] = useState({
    businessName: '',
    contactPerson: '',
    address: '',
    phone: '',
    email: '',
    businessType: '',
    notes: '',
    salesmanId: currentUser?.id || ''
  });

  useEffect(() => {
    fetchBusinessTypes();
  }, []);

  useEffect(() => {
    if (customer) {
      setFormData({
        businessName: customer.businessName,
        contactPerson: customer.contactPerson,
        address: customer.address,
        phone: customer.phone,
        email: customer.email,
        businessType: customer.businessType,
        notes: customer.notes,
        salesmanId: customer.salesmanId
      });
    } else {
      setFormData({
        businessName: '',
        contactPerson: '',
        address: '',
        phone: '',
        email: '',
        businessType: 'Tavern',
        notes: '',
        salesmanId: currentUser?.id || ''
      });
    }
  }, [customer, currentUser]);

  const fetchBusinessTypes = async () => {
    try {
      const { data, error } = await supabase
        .from('business_types')
        .select('*')
        .order('name');
      
      if (error) {
        console.error('Error fetching business types:', error);
        // Fallback to default types if database fetch fails
        setBusinessTypes([
          {id: '1', name: 'Tavern'}, 
          {id: '2', name: 'Restaurant'}, 
          {id: '3', name: 'Supermarket'}
        ]);
        return;
      }
      setBusinessTypes(data || []);
    } catch (error) {
      console.error('Error fetching business types:', error);
      // Fallback to default types if database fetch fails
      setBusinessTypes([
        {id: '1', name: 'Tavern'}, 
        {id: '2', name: 'Restaurant'}, 
        {id: '3', name: 'Supermarket'}
      ]);
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.businessType) {
      alert('Please select a business type');
      return;
    }
    await onSave(formData);
  };

  // Create unique business types list to avoid duplicates
  const uniqueBusinessTypes = businessTypes.reduce((acc: BusinessType[], current) => {
    const exists = acc.find(item => item.name === current.name);
    if (!exists) {
      acc.push(current);
    }
    return acc;
  }, []);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={`${isMobile ? 'max-w-[95vw] max-h-[90vh]' : 'sm:max-w-[500px]'}`}>
        <DialogHeader>
          <DialogTitle>{customer ? 'Edit Customer' : 'Add Customer'}</DialogTitle>
        </DialogHeader>
        
        <ScrollArea className={isMobile ? 'max-h-[60vh]' : 'max-h-[70vh]'}>
          <form onSubmit={handleSubmit} className="space-y-4 pr-4">
            <div className="space-y-2">
              <Label htmlFor="businessName">Business Name</Label>
              <Input
                id="businessName"
                value={formData.businessName}
                onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contactPerson">Contact Person</Label>
              <Input
                id="contactPerson"
                value={formData.contactPerson}
                onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="businessType">Business Type</Label>
              <Select value={formData.businessType} onValueChange={(value) => setFormData({ ...formData, businessType: value })} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select business type" />
                </SelectTrigger>
                <SelectContent>
                  {uniqueBusinessTypes.map((type) => (
                    <SelectItem key={type.id} value={type.name}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
              />
            </div>
          </form>
        </ScrollArea>
        
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" onClick={handleSubmit}>{customer ? 'Update' : 'Add'} Customer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerForm;